import produce from "immer";
import { Game } from "shared/games/types/Game";
import { GameRound } from "shared/games/types/GameRound";
import { Technique } from "shared/games/technique";
import { Difficulty } from "shared/games/Difficulty";
import toggleSetItem from "util/toggleSetItem";
import Instrument from "shared/games/instruments/Instrument";
import { getGameDescription } from "shared/games/helpers";
import allGamesByTechnique from "shared/games/allGamesByTechnique";
import { useMemo } from "react";

interface Params {
  value: GameRound;
  onChange: (value: GameRound) => void;
}

export default function useOnChangeHandler({ value, onChange }: Params) {
  const { techniques, games, instruments } = useMemo(
    () => ({
      techniques: new Set(value.techniques),
      games: new Set(value.games),
      instruments: new Set(value.instruments),
    }),
    [value]
  );

  return {
    onDifficultyChange(difficulty: Difficulty) {
      if (value.difficulty === difficulty) {
        return;
      }

      const nextValue = produce(value, (nextValue) => {
        nextValue.difficulty = difficulty;
      });

      onChange(nextValue);
    },

    onTechniqueToggle(technique: Technique) {
      const nextValue = produce(value, (nextValue) => {
        const previouslySelected = techniques.has(technique);
        const currentTechniquesGames = allGamesByTechnique(technique).map(
          ({ game }) => game
        );

        if (previouslySelected) {
          // remove all of the games owned by the technique
          currentTechniquesGames.forEach((game) => games.delete(game));
        } else {
          // add all of the games owned by the technique
          currentTechniquesGames.forEach((game) => games.add(game));
        }

        nextValue.techniques = Array.from(toggleSetItem(techniques, technique));
        nextValue.games = Array.from(games);
      });

      onChange(nextValue);
    },

    onGameToggle(game: Game) {
      const gameDesc = getGameDescription(game);

      const nextValue = produce(value, (nextValue) => {
        const previouslySelected = games.has(game);

        // if no other game owned by the technique is selected, remove the technique
        if (previouslySelected) {
          games.delete(game);

          const techniqueGames = allGamesByTechnique(gameDesc.technique).map(
            ({ game }) => game
          );

          // if no other game owned by the technique is selected, remove the technique
          if (!techniqueGames.some((g) => games.has(g))) {
            techniques.delete(gameDesc.technique);
          }
        }

        // if technique not already selected add it
        else {
          techniques.add(gameDesc.technique);
          games.add(game);
        }

        nextValue.techniques = Array.from(techniques);
        nextValue.games = Array.from(games);
      });

      onChange(nextValue);
    },

    onInstrumentToggle(instrument: Instrument) {
      const nextValue = produce(value, (nextValue) => {
        nextValue.instruments = Array.from(
          toggleSetItem(instruments, instrument)
        );
      });

      onChange(nextValue);
    },
  };
}
